import moment from "moment";
import { BufferWriter, Writer } from "protobufjs";
import { NotificationClient } from "../../../config/view_const/notification";
import { AppError, ErrorType } from "../../../domain/model/app_error";
import { FrontDate } from "../../../domain/model/frontDate";
import { Project } from "../../../domain/model/project";
import {
  IProjectRepository,
  TCreateProjectParams,
  TGetAllBySearchParams,
  TGetCountBySearchParams,
  TUpdateProjectParams,
} from "../../../domain/repository/project_repository";
import { IApiClient } from "../client";
import { api, common, entity } from "../rpc/api";
import { getToken } from "./bind_token";
import {
  ProjectConvertResponse,
  ProjectConvertResponseList,
} from "./project_api_convert";

import ProjectID = entity.ProjectID;
import ProjectIDList = entity.ProjectIDList;
import ProjectList = entity.ProjectList;
import CustomerID = entity.CustomerID;
import SearchProjectRequest = api.SearchProjectRequest;
import CreateProjectRequest = api.CreateProjectRequest;
import UpdateProjectRequest = api.UpdateProjectRequest;
import SearchProjectCountRequest = api.SearchProjectCountRequest;

import Empty = common.Empty;
import Pager = common.Pager;
import Count = common.Count;

class ProjectAPI implements IProjectRepository {
  // operatingDate: Frontend => BackEnd における型変換
  private static operatingDateConvertToString = (
    frontDate: FrontDate | undefined
  ): string => {
    if (!frontDate) {
      return ``;
    } else {
      return frontDate.year === "" && frontDate.month === ""
        ? ``
        : moment()
            .year(Number(frontDate.year.toString()))
            .month(Number(frontDate.month.toString()) - 1)
            .format("YYYY-MM-DD")
            .toString();
    }
  };

  constructor(readonly apiClient: IApiClient) {}

  public getAllByIDs(ids: string[]): Promise<Project[]> {
    return new Promise<Project[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: ProjectIDList = new ProjectIDList();
          req.ids = ids;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/GetAllByIDs",
            token,
            ProjectIDList.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: ProjectList = ProjectList.decode(binary);
          // BE=>FE 型変換
          const resConverted: ProjectConvertResponseList =
            ProjectConvertResponseList.from(res);
          const items: Project[] = resConverted.items.map(
            (item: ProjectConvertResponse): Project => {
              return Project.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public getAllByNewlyArrived(
    page: number,
    offset: number
  ): Promise<Project[]> {
    return new Promise<Project[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: Pager = new Pager();
          req.page = page;
          req.offset = offset;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/GetAllByNewlyArrived",
            token,
            Pager.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: ProjectList = ProjectList.decode(binary);
          // BE=>FE 型変換
          const resConverted: ProjectConvertResponseList =
            ProjectConvertResponseList.from(res);
          const items: Project[] = resConverted.items.map(
            (item: ProjectConvertResponse): Project => {
              return Project.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public getAllByRecommend(): Promise<Project[]> {
    return new Promise<Project[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: Empty = new Empty();
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/GetAllByRecommend",
            token,
            Empty.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: ProjectList = ProjectList.decode(binary);
          // BE=>FE 型変換
          const resConverted: ProjectConvertResponseList =
            ProjectConvertResponseList.from(res);
          const items: Project[] = resConverted.items.map(
            (item: ProjectConvertResponse): Project => {
              return Project.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public getAllBySearch(
    getAllBySearchParams: TGetAllBySearchParams
  ): Promise<Project[]> {
    return new Promise<Project[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          // operatingDate: Frontend => BackEnd における型変換
          const operatingDate_string: string =
            ProjectAPI.operatingDateConvertToString(
              getAllBySearchParams.operatingDate
            );

          const req: SearchProjectRequest = new SearchProjectRequest();
          const pager: Pager = new Pager();

          pager.page = getAllBySearchParams.page;
          pager.offset = getAllBySearchParams.offset;

          req.pager = pager;
          req.q = getAllBySearchParams.q;
          req.occupation = getAllBySearchParams.occupation;
          req.workingHourRange = getAllBySearchParams.workingHourRange;
          req.paymentRange = getAllBySearchParams.paymentRange;
          req.workingDuration = getAllBySearchParams.workingDuration;
          req.operatingDate = operatingDate_string;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/GetAllBySearch",
            token,
            SearchProjectRequest.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: ProjectList = ProjectList.decode(binary);
          // BE=>FE 型変換
          const resConverted: ProjectConvertResponseList =
            ProjectConvertResponseList.from(res);
          const items: Project[] = resConverted.items.map(
            (item: ProjectConvertResponse): Project => {
              return Project.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public getAllByClient(page: number, offset: number): Promise<Project[]> {
    return new Promise<Project[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: Pager = new Pager();
          req.page = page;
          req.offset = offset;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/GetAll",
            token,
            Pager.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: ProjectList = ProjectList.decode(binary);
          // BE=>FE 型変換
          const resConverted: ProjectConvertResponseList =
            ProjectConvertResponseList.from(res);
          const items: Project[] = resConverted.items.map(
            (item: ProjectConvertResponse): Project => {
              return Project.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public getAllByClientExcludeAlreadyEntry(
    customerId: string
  ): Promise<Project[]> {
    return new Promise<Project[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: CustomerID = new CustomerID();
          req.id = customerId;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/GetAllExcludeAlreadyEntry",
            token,
            CustomerID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: ProjectList = ProjectList.decode(binary);
          // BE=>FE 型変換
          const resConverted: ProjectConvertResponseList =
            ProjectConvertResponseList.from(res);
          const items: Project[] = resConverted.items.map(
            (item: ProjectConvertResponse): Project => {
              return Project.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public getAllByEntry(page: number, offset: number): Promise<Project[]> {
    return new Promise<Project[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: Pager = new Pager();
          req.page = page;
          req.offset = offset;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/GetAllByEntry",
            token,
            Pager.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: ProjectList = ProjectList.decode(binary);
          // BE=>FE 型変換
          const resConverted: ProjectConvertResponseList =
            ProjectConvertResponseList.from(res);
          const items: Project[] = resConverted.items.map(
            (item: ProjectConvertResponse): Project => {
              return Project.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public getAllByEntryNumOrder(
    page: number,
    offset: number
  ): Promise<Project[]> {
    return new Promise<Project[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: Pager = new Pager();
          req.page = page;
          req.offset = offset;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/GetAllByEntryNumOrder",
            token,
            Pager.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: ProjectList = ProjectList.decode(binary);
          // BE=>FE 型変換
          const resConverted: ProjectConvertResponseList =
            ProjectConvertResponseList.from(res);
          const items: Project[] = resConverted.items.map(
            (item: ProjectConvertResponse): Project => {
              return Project.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public get(projectID: string): Promise<Project> {
    return new Promise<Project>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: ProjectID = new ProjectID();
          req.id = projectID;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/Get",
            token,
            ProjectID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.Project = entity.Project.decode(binary);
          // BE=>FE 型変換
          const resConverted: ProjectConvertResponse =
            ProjectConvertResponse.from(res);
          const item: Project = Project.from(resConverted);
          resolve(item);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public getCountByNewlyArrived = (): Promise<number | Long> => {
    return new Promise<number | Long>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: Empty = new Empty();
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/GetCountByNewlyArrived",
            token,
            Empty.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: Count = Count.decode(binary);
          const item: number | Long = res.count;
          resolve(item);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  };

  public getCountBySearch = (
    getCountBySearchParams: TGetCountBySearchParams
  ): Promise<number | Long> => {
    return new Promise<number | Long>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          // operatingDate: Frontend => BackEnd における型変換
          const operatingDate_string: string =
            ProjectAPI.operatingDateConvertToString(
              getCountBySearchParams.operatingDate
            );

          const req: SearchProjectCountRequest =
            new SearchProjectCountRequest();
          req.q = getCountBySearchParams.q;
          req.occupation = getCountBySearchParams.occupation;
          req.workingHourRange = getCountBySearchParams.workingHourRange;
          req.paymentRange = getCountBySearchParams.paymentRange;
          req.workingDuration = getCountBySearchParams.workingDuration;
          req.operatingDate = operatingDate_string;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/GetCountBySearch",
            token,
            SearchProjectCountRequest.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: Count = Count.decode(binary);
          const item: number | Long = res.count;
          resolve(item);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  };

  public getCountByClient = (): Promise<number | Long> => {
    return new Promise<number | Long>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: Empty = new Empty();
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/GetCount",
            token,
            Empty.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: Count = Count.decode(binary);
          const item: number | Long = res.count;
          resolve(item);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  };

  public getCountByEntry = (): Promise<number | Long> => {
    return new Promise<number | Long>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: Empty = new Empty();
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/GetCountByEntry",
            token,
            Empty.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: Count = Count.decode(binary);
          const item: number | Long = res.count;
          resolve(item);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  };

  public create(createProjectParams: TCreateProjectParams): Promise<Project> {
    return new Promise<Project>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          // operatingDate: Frontend => BackEnd における型変換
          const operatingDate_string: string =
            ProjectAPI.operatingDateConvertToString(
              createProjectParams.operatingDate
            );

          const req: CreateProjectRequest = new CreateProjectRequest();
          req.name = createProjectParams.name;
          req.description = createProjectParams.description;
          req.background = createProjectParams.background;
          req.preferentialExperience =
            createProjectParams.preferentialExperience;
          req.thumbnailURL = createProjectParams.thumbnailURL;
          req.place = createProjectParams.place;
          req.workingHourRange = createProjectParams.workingHourRange;
          req.paymentRange = createProjectParams.paymentRange;
          req.workingDuration = createProjectParams.workingDuration;
          req.occupations = createProjectParams.occupations;
          req.operatingDate = operatingDate_string;
          req.isHideCompany = createProjectParams.isHideCompany;
          req.isOpen = createProjectParams.isOpen;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/Create",
            token,
            CreateProjectRequest.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.Project = entity.Project.decode(binary);
          // BE=>FE 型変換
          const resConverted: ProjectConvertResponse =
            ProjectConvertResponse.from(res);
          const item: Project = Project.from(resConverted);
          resolve(item);
        })
        .catch((error: Error): void => {
          if (error instanceof AppError) {
            if (error.type === ErrorType.API_REQUEST_403) {
              reject(
                new AppError(
                  error.code,
                  ErrorType.FAIL_PROJECT_OPEN,
                  NotificationClient.failureStatusOpenProject
                )
              );
            } else {
              reject(error);
            }
          }
        });
    });
  }

  public update(updateProjectParams: TUpdateProjectParams): Promise<Project> {
    return new Promise<Project>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          // operatingDate: Frontend => BackEnd における型変換
          const operatingDate_string: string =
            ProjectAPI.operatingDateConvertToString(
              updateProjectParams.operatingDate
            );

          const req: UpdateProjectRequest = new UpdateProjectRequest();
          req.id = updateProjectParams.id;
          req.name = updateProjectParams.name;
          req.description = updateProjectParams.description;
          req.background = updateProjectParams.background;
          req.preferentialExperience =
            updateProjectParams.preferentialExperience;
          req.thumbnailURL = updateProjectParams.thumbnailURL;
          req.place = updateProjectParams.place;
          req.workingHourRange = updateProjectParams.workingHourRange;
          req.paymentRange = updateProjectParams.paymentRange;
          req.workingDuration = updateProjectParams.workingDuration;
          req.occupations = updateProjectParams.occupations;
          req.operatingDate = operatingDate_string;
          req.isHideCompany = updateProjectParams.isHideCompany;
          const writer: BufferWriter | Writer = Writer.create();

          return this.apiClient.postWithToken(
            "/api.ProjectService/Update",
            token,
            UpdateProjectRequest.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.Project = entity.Project.decode(binary);
          // BE=>FE 型変換
          const resConverted: ProjectConvertResponse =
            ProjectConvertResponse.from(res);
          const item: Project = Project.from(resConverted);
          resolve(item);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public draft(projectID: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: ProjectID = new ProjectID();
          req.id = projectID;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/Draft",
            token,
            ProjectID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          resolve();
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public open(projectID: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: ProjectID = new ProjectID();
          req.id = projectID;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/Open",
            token,
            ProjectID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          resolve();
        })
        .catch((error: Error): void => {
          if (error instanceof AppError) {
            if (error.type === ErrorType.API_REQUEST_403) {
              reject(
                new AppError(
                  error.code,
                  ErrorType.FAIL_PROJECT_OPEN,
                  NotificationClient.failureStatusOpenProject
                )
              );
            } else {
              reject(error);
            }
          }
        });
    });
  }

  public close(projectID: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: ProjectID = new ProjectID();
          req.id = projectID;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/Close",
            token,
            ProjectID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          resolve();
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public onlyScout(projectID: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: ProjectID = new ProjectID();
          req.id = projectID;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/OnlyScout",
            token,
            ProjectID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          resolve();
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public delete(projectID: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: ProjectID = new ProjectID();
          req.id = projectID;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/Delete",
            token,
            ProjectID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          resolve();
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public generateProjectInfo(requirements: string): Promise<{
    title: string;
    description: string;
    background: string;
    preferentialExperience: string;
  }> {
    return new Promise<{
      title: string;
      description: string;
      background: string;
      preferentialExperience: string;
    }>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: api.GenerateProjectInfoRequest =
            new api.GenerateProjectInfoRequest();
          req.requirements = requirements;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/GenerateProjectInfo",
            token,
            api.GenerateProjectInfoRequest.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: api.GenerateProjectInfoResponse =
            api.GenerateProjectInfoResponse.decode(binary);
          resolve({
            title: res.title,
            description: res.description,
            background: res.background,
            preferentialExperience: res.preferentialExperience,
          });
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public copy(projectID: string): Promise<Project> {
    return new Promise<Project>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: ProjectID = new ProjectID();
          req.id = projectID;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/Copy",
            token,
            ProjectID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.Project = entity.Project.decode(binary);
          // BE=>FE 型変換
          const resConverted: ProjectConvertResponse =
            ProjectConvertResponse.from(res);
          const item: Project = Project.from(resConverted);
          resolve(item);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public sharing(req: api.ISharingProjectRequest): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ProjectService/Sharing",
            token,
            api.SharingProjectRequest.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          resolve();
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }
}

const createProjectAPI = (apiClient: IApiClient): IProjectRepository => {
  return new ProjectAPI(apiClient);
};

export { createProjectAPI };



// WEBPACK FOOTER //
// ./src/infra/api/service/project_api.ts